import './BubbleSearchSelect.scss';
import React, { useState, useEffect, useRef } from 'react';
import { BubbleDebounce, BubbleInput } from '@go-client/components/BubbleUI';

interface BubbleSearchSelectPropsBase<T> {
  variant?: 'navy' | 'forest' | 'velvet' | 'titanium' | 'turquoise';
  appearance?: 'bare' | 'rounded';
  block?: boolean;
  inputClassName?: string;
  inputStyle?: React.CSSProperties;
  menuClassName?: string;
  suggestionClassName?: string;

  // Function used to get results from an async method
  fetchOptions: (query: string) => Promise<T[]>;
  // Function used to render items
  renderItem?: (item: T) => React.ReactNode;
  // Function used to control the value from the item
  getItemValue: (item: T) => string;
  // Event fired when an item is selected
  onItemSelected: (item: T | undefined) => void;
  placeholder?: string;
  suggestionsText?: string;

  minLength?: number;
  startingQuery?: string;
}

export type BubbleSearchSelectProps<T> = React.HTMLAttributes<HTMLDivElement> &
  BubbleSearchSelectPropsBase<T>;

export default function BubbleSearchSelect<T>(props: BubbleSearchSelectProps<T>) {
  const {
    variant,
    appearance,
    block,
    suggestionsText,
    inputClassName: propInputClassName,
    inputStyle: propInputStyle,
    menuClassName: propMenuClassName,
    suggestionClassName: propSuggestionClassName,

    // from div element itself
    className: propClassName,
    style: propStyle,

    fetchOptions,
    renderItem,
    getItemValue,
    onItemSelected,
    placeholder,
    minLength = 3,
    startingQuery,
    ...rest
  } = props;

  const [query, setQuery] = useState('');
  const [selectedValue, setSelectedValue] = useState('');
  const [results, setResults] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [initialFetched, setInitialFetched] = useState(false);

  const [timer, setTimer] = useState<ReturnType<typeof setTimeout> | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);

  async function fetchData(query: string, skipLengthCheck = false, autoSelectFirst = false) {
    if (!skipLengthCheck && query.length < minLength) {
      setResults([]);
      onItemSelected(undefined);
      return;
    }

    setLoading(true);

    try {
      const res = await fetchOptions(query);
      if (autoSelectFirst && res.length === 1) {
        handleSelect(res[0]);
      } else {
        setResults(res);
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchData(query).catch(console.error);
  }, [query]);

  useEffect(() => {
    if (initialFetched || !startingQuery) return;

    setInitialFetched(true);

    fetchData(startingQuery, true, true).catch(console.error);
  }, [startingQuery]);

  function handleSelect(item: T) {
    onItemSelected(item);
    setSelectedValue(getItemValue(item));
    setResults([]);
  }

  const classes = ['bubble-typeahead'];
  if (block) classes.push('full');
  if (appearance) classes.push(appearance);
  if (variant) classes.push(variant);
  if (propClassName) classes.push(propClassName);

  let style: React.CSSProperties = {};
  if (propStyle) style = { ...style, ...propStyle };

  const inputClasses = ['bt-input', 'w-full', 'bubble-search-select'];
  if (propInputClassName) inputClasses.push(propInputClassName);

  let inputStyle: React.CSSProperties = {};
  if (propInputStyle) inputStyle = { ...inputStyle, ...propInputStyle };

  const menuClasses = ['bubble-suggestions'];
  if (propMenuClassName) menuClasses.push(propMenuClassName);

  const suggestionClasses = ['suggestions-item', 's-tab'];
  if (propSuggestionClassName) suggestionClasses.push(propSuggestionClassName);

  const error =
    query.length < minLength
      ? `Please enter at minimum ${minLength} characters`
      : loading
      ? 'Searching...'
      : results.length === 0
      ? suggestionsText || 'No results found'
      : undefined;

  function delayedBlur() {
    // a timeout is necessary because clicking the menu items 'unfocuses' the input,
    // which in turn hides the menu before the click event can be registered.
    if (timer) {
      clearTimeout(timer);
    }
    // Delay hiding the menu to allow click event to register
    const newTimer = setTimeout(() => {
      setShowMenu(false);
    }, 200);
    setTimer(newTimer);
  }

  return (
    <div ref={containerRef} {...rest} className={classes.join(' ')} style={style}>
      <BubbleDebounce
        onTrigger={[
          (search: React.ChangeEvent<HTMLInputElement>) => setQuery(search.target.value),
          500,
        ]}
      >
        {({ debounce }) => (
          <BubbleInput
            type="search"
            variant={variant}
            block
            value={selectedValue}
            className="w-full"
            inputClassName={inputClasses.join(' ')}
            style={inputStyle}
            onValue={debounce}
            placeholder={placeholder || 'Type to search...'}
            showSpinner={loading}
            onFocus={() => setShowMenu(true)}
            onBlur={delayedBlur}
          />
        )}
      </BubbleDebounce>

      {showMenu && (
        <div className={menuClasses.join(' ')} style={{ minHeight: '37px' }}>
          {error && (
            <div className="suggestions-option">
              <div className="suggestions-empty">{error}</div>
            </div>
          )}
          {results.map((item, idx) => (
            <div key={idx} onClick={() => handleSelect(item)} className={`suggestions-option`}>
              <div className={suggestionClasses.join(' ')} tabIndex={-1}>
                {renderItem ? renderItem(item) : getItemValue(item)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
